import React, { Component, ErrorInfo, ReactNode } from 'react';
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useLayout, getDensityClasses } from '../contexts/LayoutContext';

interface Props {
  children: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class QuestionnaireErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Questionnaire ErrorBoundary caught an error:', error, errorInfo);
    
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render() {
    if (this.state.hasError) {
      return <QuestionnaireErrorFallback onRetry={this.handleRetry} />;
    }

    return this.props.children;
  }
}

interface QuestionnaireErrorFallbackProps {
  onRetry: () => void;
}

const QuestionnaireErrorFallback: React.FC<QuestionnaireErrorFallbackProps> = ({ onRetry }) => {
  const { density } = useLayout();
  const densityClasses = getDensityClasses(density);

  return (
    <div className={`bg-red-50 border border-red-200 rounded-lg ${densityClasses.card}`}>
      <div className="flex items-start">
        <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
        <div className="flex-1">
          <h3 className={`font-medium text-red-800 ${densityClasses.text}`}>
            Questionnaire Error
          </h3>
          <p className={`mt-1 text-red-700 ${densityClasses.text}`}>
            There was a problem loading the questionnaire. This might be due to a network issue or data problem.
          </p>
          <div className={`mt-3 flex space-x-3 ${densityClasses.spacing}`}>
            <button
              onClick={onRetry}
              className={`inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors ${densityClasses.button}`}
            >
              <ArrowPathIcon className="w-4 h-4 mr-1" />
              Retry
            </button>
            <button
              onClick={() => window.location.reload()}
              className={`inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors ${densityClasses.button}`}
            >
              Reload Page
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const QuestionnaireErrorBoundary: React.FC<Props> = (props) => {
  return <QuestionnaireErrorBoundaryClass {...props} />;
};

export default QuestionnaireErrorBoundary;
